import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const PLACEMENT_ARROW = {
	top: '-bottom-1.5 left-1/2 -translate-x-1/2',
	bottom: '-top-1.5 left-1/2 -translate-x-1/2',
	left: 'left-full top-1/2 -translate-y-1/2 -ml-1.5',
	right: 'right-full top-1/2 -translate-y-1/2 -mr-1.5'
};

/**
 * Shared tooltip. Prefer a portal so modal overflow / stacking contexts cannot clip it
 * (e.g. Document Settings “?” help next to Change document name).
 */
const Tooltip = ({
	children,
	content,
	isOnClick = false,
	wrapperClassName = '',
	placement = 'top',
	/** When true (default), render tooltip into document.body to avoid overflow clipping */
	usePortal = true
}) => {
	const [open, setOpen] = useState(false);
	const [coords, setCoords] = useState(null);
	const rootRef = useRef(null);
	const portalRef = useRef(null);

	const measure = useCallback(() => {
		const el = rootRef.current;
		if (!el) return null;
		const rect = el.getBoundingClientRect();
		const gap = 12;
		let top = 0;
		let left = 0;
		let transform = '';

		switch (placement) {
			case 'bottom':
				top = rect.bottom + gap;
				left = rect.left + rect.width / 2;
				transform = 'translateX(-50%)';
				break;
			case 'left':
				top = rect.top + rect.height / 2;
				left = rect.left - gap;
				transform = 'translate(-100%, -50%)';
				break;
			case 'right':
				top = rect.top + rect.height / 2;
				left = rect.right + gap;
				transform = 'translateY(-50%)';
				break;
			default:
				top = rect.top - gap;
				left = rect.left + rect.width / 2;
				transform = 'translate(-50%, -100%)';
				break;
		}

		return { top, left, transform };
	}, [placement]);

	const updatePosition = useCallback(() => {
		const next = measure();
		if (next) setCoords(next);
	}, [measure]);

	const show = useCallback(() => {
		const next = measure();
		if (next) setCoords(next);
		setOpen(true);
	}, [measure]);

	const hide = useCallback(() => {
		if (!isOnClick) setOpen(false);
	}, [isOnClick]);

	const toggle = useCallback(
		(e) => {
			if (!isOnClick) return;
			e.preventDefault();
			e.stopPropagation();
			setOpen((prev) => {
				if (prev) return false;
				const next = measure();
				if (next) setCoords(next);
				return true;
			});
		},
		[isOnClick, measure]
	);

	useLayoutEffect(() => {
		if (!open) {
			setCoords(null);
			return;
		}
		updatePosition();
		const onScrollOrResize = () => updatePosition();
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);
		return () => {
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [open, updatePosition]);

	useEffect(() => {
		if (!isOnClick || !open) return;
		const handleClickOutside = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				if (portalRef.current && portalRef.current.contains(e.target)) return;
				setOpen(false);
			}
		};
		// Defer so the opening click does not immediately close the tip
		const id = window.setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);
		return () => {
			window.clearTimeout(id);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOnClick, open]);

	const tipBody =
		open &&
		content &&
		(!usePortal || coords) && (
			<div
				ref={portalRef}
				role="tooltip"
				style={{
					backdropFilter: 'blur(20px)',
					...(usePortal && coords
						? {
								position: 'fixed',
								top: coords.top,
								left: coords.left,
								transform: coords.transform,
								zIndex: 100000
							}
						: undefined)
				}}
				className={
					usePortal
						? 'w-[220px] max-w-[min(220px,calc(100vw-24px))] rounded-md bg-[#87868B] text-white text-center py-2 px-4 text-xs font-medium pointer-events-none shadow-lg'
						: `absolute w-[200px] z-[100000] rounded-md bg-[#87868B] text-white text-center py-2 px-4 text-xs font-medium
						${placement === 'bottom' ? 'top-full left-1/2 mt-3 -translate-x-1/2' : ''}
						${placement === 'left' ? 'right-full top-1/2 -translate-y-1/2 mr-3' : ''}
						${placement === 'right' ? 'left-full top-1/2 -translate-y-1/2 ml-3' : ''}
						${placement === 'top' || !['bottom', 'left', 'right'].includes(placement) ? 'bottom-full left-1/2 mb-3 -translate-x-1/2' : ''}
					`
				}
			>
				{!usePortal && (
					<span
						className={`absolute ${PLACEMENT_ARROW[placement] || PLACEMENT_ARROW.top} z-0 h-3 w-3 rotate-45 bg-[#87868B]`}
					/>
				)}
				{content}
			</div>
		);

	return (
		<div
			ref={rootRef}
			className={`relative inline-flex ${!isOnClick ? 'group' : ''} ${wrapperClassName}`}
			onClick={toggle}
			onMouseEnter={!isOnClick ? show : undefined}
			onMouseLeave={!isOnClick ? hide : undefined}
			onFocus={!isOnClick ? show : undefined}
			onBlur={!isOnClick ? hide : undefined}
		>
			<span className="inline-flex items-center">{children}</span>
			{usePortal && typeof document !== 'undefined' && tipBody
				? createPortal(tipBody, document.body)
				: tipBody}
		</div>
	);
};

export default Tooltip;
